import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';
import { DashboardRequestParameters } from '@portal/app/shared/types';
import { MiaFilterContext } from '@portal/app/shared/contracts/mia/mia-contracts.types';

@Injectable({
  providedIn: 'root'
})
export class ViewStore {
  public selectedBCPDId = new BehaviorSubject<DashboardRequestParameters>({
    clientId: 0,
    brandId: 0,
    literalId: '',
    productId: ''
  });

  /**
   * Canonical dashboard literal_id for the active page; set by each page host in
   * ngOnInit and cleared in ngOnDestroy (empty = no dashboard active). Source of
   * truth for MiaContextService — prefer this over URL parsing (route data / DB
   * catalog, not URL segment heuristics).
   */
  readonly currentDashboardLiteralId = signal<string>('');

  /**
   * Page-level entity / filter hints for MIA (`page_data.filter_context`, PMDEV-9680)
   * and open page state (`page_data.page_context`, PMDEV-9905). Pages write both
   * through `injectMiaPageContext()`, which clears them when the page is destroyed.
   */
  readonly pageFilterContext = signal<MiaFilterContext>({});
  readonly pageContext = signal<Record<string, unknown>>({});

  setCurrentDashboardLiteralId(literalId: string): void {
    this.currentDashboardLiteralId.set(literalId);
  }

  setPageFilterContext(ctx: MiaFilterContext): void {
    this.pageFilterContext.set({ ...ctx });
  }

  clearPageFilterContext(): void {
    this.pageFilterContext.set({});
  }

  /** `undefined` removes the key. */
  setPageContext(key: string, value: unknown): void {
    this.pageContext.update((ctx) => {
      const next = { ...ctx };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  private readonly titleSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  get title(): Observable<string> {
    return this.titleSubject.asObservable();
  }

  changeTitle(value: string): Observable<string> {
    const obs = new Observable((subscriber: Subscriber<string>) => {
      subscriber.next(value);
      subscriber.complete();
    });

    obs.subscribe({
      next: () => this.titleSubject.next(value)
    });

    return obs;
  }

  setBrandClientProductDashboardId(data: DashboardRequestParameters): void {
    this.selectedBCPDId.next(data);
  }
}
